import { InstrumentCategoryEnum, Inventory, SelectedAccountOrClient } from '../../types';
import { BaseCurrency, isDefined, Loadable, LoadableState } from '../../utils';
import { getInitialPNl } from './inventoryUtils';

type GetFloatingValueParams = {
  realtimeInventories: LoadableState<Array<Inventory>>;
  selectedAccountOrClient?: SelectedAccountOrClient;
  getCurrencyExchangeRateRealTime: (base: string, quote: string) => number;
  baseCurrency: string;
  getClosePriceByRic?: (ric: string) => number | undefined;
};

const getFilteredInventories = (
  realtimeInventories: Array<Inventory>,
  selectedAccountOrClient: SelectedAccountOrClient,
): Array<Inventory> => {
  return isDefined(selectedAccountOrClient.account)
    ? realtimeInventories.filter(_ => _.accountNumber === selectedAccountOrClient.account!.accountNumber)
    : realtimeInventories.filter(_ => _.clientId === selectedAccountOrClient.client!);
};

export const getFloatingValue = ({
  realtimeInventories,
  selectedAccountOrClient,
  getCurrencyExchangeRateRealTime,
  baseCurrency,
  getClosePriceByRic,
}: GetFloatingValueParams): LoadableState<number> => {
  if (!realtimeInventories.isLoaded || !selectedAccountOrClient) {
    return Loadable.loading;
  }

  const filteredInventories = getFilteredInventories(realtimeInventories, selectedAccountOrClient);

  const result = filteredInventories.reduce((res, inventory) => {
    const { pNL } = getInitialPNl({
      instrumentCategory: inventory.instrumentCategory as InstrumentCategoryEnum,
      position: inventory.position,
      marketPriceSingle: inventory.marketPriceSingle,
      initPriceValue: inventory.initPriceValue,
      rate: getCurrencyExchangeRateRealTime(inventory.marketPriceCcy, inventory.initCcy),
      grossProfitLoss: inventory.initCcy === baseCurrency ? inventory.grossProfitLossHuf : inventory.grossProfitLoss,
      ric: inventory.ric,
      closePrice: inventory.ric && getClosePriceByRic ? getClosePriceByRic(inventory.ric) : undefined,
    });
    if (pNL === undefined) {
      return res;
    } else {
      const rate = getCurrencyExchangeRateRealTime(inventory.initCcy, BaseCurrency);
      return res + pNL * rate;
    }
  }, 0);

  return Loadable.loaded(result);
};
